export const ADMIN_CAPABILITY_VISIBILITY_PANEL_TEST_ID = 'admin-capability-visibility-panel';
export const ADMIN_CAPABILITY_VISIBILITY_EMPTY_TEST_ID = 'admin-capability-visibility-empty';
export const ADMIN_CAPABILITY_VISIBILITY_LOADING_TEST_ID = 'admin-capability-visibility-loading';

export function adminCapabilityCardTestId(id) {
  return `admin-capability-card-${id}`;
}

export function readFrontendCapabilityFlags(env = process.env) {
  return {
    marketingStudioEnabled: env.REACT_APP_MARKETING_STUDIO_ENABLED === 'true',
    privateModeEnabled: env.REACT_APP_PRIVATE_MODE === 'true',
  };
}

export const ADMIN_CAPABILITY_STATUS_META = Object.freeze({
  'visible-authenticated': {
    labelKey: 'admin.capabilities.status_visible_authenticated',
    label: 'Visible to signed-in users',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  'visible-live': {
    labelKey: 'admin.capabilities.status_visible_live',
    label: 'Live guard active',
    className: 'bg-signal-blue/10 text-signal-blue border-signal-blue/20',
  },
  'deployed-hidden': {
    labelKey: 'admin.capabilities.status_deployed_hidden',
    label: 'Deployed hidden',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  'flag-off': {
    labelKey: 'admin.capabilities.status_flag_off',
    label: 'Flag off',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  'admin-only': {
    labelKey: 'admin.capabilities.status_admin_only',
    label: 'Admin only',
    className: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  'architecture-only': {
    labelKey: 'admin.capabilities.status_architecture_only',
    label: 'Architecture only',
    className: 'bg-stone-100 text-stone-700 border-stone-200',
  },
  'public-mode': {
    labelKey: 'admin.capabilities.status_public_mode',
    label: 'Public mode',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
});

export function buildAdminCapabilityCards(flags = readFrontendCapabilityFlags()) {
  const marketingStudioEnabled = Boolean(flags.marketingStudioEnabled);
  const privateModeEnabled = Boolean(flags.privateModeEnabled);

  return [
    {
      id: 'marketing-studio',
      icon: 'megaphone',
      titleKey: 'admin.capabilities.marketing_studio.title',
      title: 'Marketing Studio',
      status: marketingStudioEnabled ? 'admin-only' : 'flag-off',
      postureKey: 'admin.capabilities.posture_admin_flagged',
      posture: 'Admin-only + feature-flagged',
      descriptionKey: 'admin.capabilities.marketing_studio.description',
      description: marketingStudioEnabled
        ? 'Admin route and header entry are enabled for approved operators.'
        : 'Code and route are deployed, but the build-time flag is off so the header entry stays hidden.',
      detailKey: 'admin.capabilities.marketing_studio.detail',
      detail: 'Existing route: /admin/marketing. Backend still fails closed unless its server flag is enabled.',
      href: '/admin/marketing',
      actionLabelKey: 'admin.capabilities.marketing_studio.action',
      actionLabel: marketingStudioEnabled ? 'Open studio' : 'Verify disabled route',
    },
    {
      id: 'digital-documents',
      icon: 'fileText',
      titleKey: 'admin.capabilities.digital_documents.title',
      title: 'Digital Documents',
      status: 'flag-off',
      postureKey: 'admin.capabilities.posture_backend_flagged',
      posture: 'Backend-only + feature-flagged',
      descriptionKey: 'admin.capabilities.digital_documents.description',
      description: 'Backend foundation is deployed hidden; document routes fail closed while the backend flag is off.',
      detailKey: 'admin.capabilities.digital_documents.detail',
      detail: 'No safe admin route exists yet. This card is status-only.',
    },
    {
      id: 'live-translation',
      icon: 'languages',
      titleKey: 'admin.capabilities.live_translation.title',
      title: 'Live Translation',
      status: 'architecture-only',
      postureKey: 'admin.capabilities.posture_held',
      posture: 'Held for provider/legal gates',
      descriptionKey: 'admin.capabilities.live_translation.description',
      description: 'Architecture is documented, but no production translation surface is enabled.',
      detailKey: 'admin.capabilities.live_translation.detail',
      detail: 'No route, provider key, voice path, or live transport is exposed.',
    },
    {
      id: 'ecosystem-watcher',
      icon: 'activity',
      titleKey: 'admin.capabilities.ecosystem_watcher.title',
      title: 'Ecosystem Watcher',
      status: 'deployed-hidden',
      postureKey: 'admin.capabilities.posture_read_only',
      posture: 'Read-only internal artifact',
      descriptionKey: 'admin.capabilities.ecosystem_watcher.description',
      description: 'Digest export code and artifacts exist in the repo, but no admin viewer is exposed yet.',
      detailKey: 'admin.capabilities.ecosystem_watcher.detail',
      detail: 'No live feed, no decisions, no automatic execution path.',
    },
    {
      id: 'experience-layer',
      icon: 'sparkles',
      titleKey: 'admin.capabilities.experience_layer.title',
      title: 'Experience Layer',
      status: 'visible-authenticated',
      postureKey: 'admin.capabilities.posture_authenticated',
      posture: 'Authenticated settings surface',
      descriptionKey: 'admin.capabilities.experience_layer.description',
      description: 'Flow navigation and the Experience settings tab are available to signed-in users.',
      detailKey: 'admin.capabilities.experience_layer.detail',
      detail: 'Settings route is existing and safe; experience data remains user-owned.',
      href: '/settings',
      actionLabelKey: 'admin.capabilities.experience_layer.action',
      actionLabel: 'Open settings',
    },
    {
      id: 'private-preview',
      icon: 'lock',
      titleKey: 'admin.capabilities.private_preview.title',
      title: 'Private Preview',
      status: privateModeEnabled ? 'visible-live' : 'public-mode',
      postureKey: 'admin.capabilities.posture_public_gate',
      posture: 'Public access guard',
      descriptionKey: 'admin.capabilities.private_preview.description',
      description: privateModeEnabled
        ? 'Anonymous browsing is gated; login and reset routes remain available.'
        : 'Anonymous browsing is not blocked in this build.',
      detailKey: 'admin.capabilities.private_preview.detail',
      detail: 'This is a read-only indicator of the current frontend build flag.',
    },
  ];
}
